/*
 * Convert a datetime-local wall-clock string (e.g. "2026-04-23T19:30") to the
 * correct UTC Instant when the user has chosen a specific IANA timezone.
 *
 * Why: <input type="datetime-local"> yields a local-naive string with no
 * offset. `new Date(localValue)` parses it in the browser's own timezone,
 * which produces a wrong Instant the moment the event timezone differs from
 * the viewer's timezone (e.g. booking an NYC show from an LA laptop).
 *
 * Strategy: compute what offset the target timezone applies at a candidate
 * UTC instant via Intl.DateTimeFormat, then adjust and re-check once to
 * settle around DST boundaries.
 */

const DATETIME_LOCAL_PATTERN = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/;

function getZoneDateTimeParts(utcMillis, timeZone) {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone,
        hourCycle: 'h23',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    }).formatToParts(new Date(utcMillis));

    const map = {};
    for (const part of parts) {
        if (part.type !== 'literal') {
            map[part.type] = part.value;
        }
    }

    return {
        year: Number(map.year),
        month: Number(map.month),
        day: Number(map.day),
        hour: map.hour === '24' ? 0 : Number(map.hour),
        minute: Number(map.minute),
        second: Number(map.second),
    };
}

function getZoneOffsetMs(utcMillis, timeZone) {
    const zoned = getZoneDateTimeParts(utcMillis, timeZone);
    const asUtcMillis = Date.UTC(
        zoned.year,
        zoned.month - 1,
        zoned.day,
        zoned.hour,
        zoned.minute,
        zoned.second,
    );
    return asUtcMillis - utcMillis;
}

function isValidLocalDateTime(year, monthIndex, day, hour, minute, second) {
    if (![year, monthIndex, day, hour, minute, second].every(Number.isFinite)) {
        return false;
    }
    const probe = new Date(Date.UTC(year, monthIndex, day, hour, minute, second));
    return probe.getUTCFullYear() === year
        && probe.getUTCMonth() === monthIndex
        && probe.getUTCDate() === day
        && probe.getUTCHours() === hour
        && probe.getUTCMinutes() === minute
        && probe.getUTCSeconds() === second;
}

function matchesRequestedWallTime(utcMillis, timeZone, expected) {
    const actual = getZoneDateTimeParts(utcMillis, timeZone);
    return actual.year === expected.year
        && actual.month === expected.month
        && actual.day === expected.day
        && actual.hour === expected.hour
        && actual.minute === expected.minute
        && actual.second === expected.second;
}

function pad2(value) {
    return String(value).padStart(2, '0');
}

/*
 * Inverse of zonedWallTimeToIso: take a UTC ISO string + IANA timezone and
 * produce a "YYYY-MM-DDTHH:mm" datetime-local string showing the wall-clock
 * time as observed in that timezone. Used to populate <input type="datetime-local">
 * when editing an existing event.
 */
export function isoToZonedWallTime(isoValue, timeZone) {
    if (typeof isoValue !== 'string' || !isoValue.trim()) return '';
    const utcMillis = Date.parse(isoValue);
    if (!Number.isFinite(utcMillis)) return '';

    const zone = typeof timeZone === 'string' && timeZone.trim() ? timeZone.trim() : null;
    try {
        const parts = zone
            ? getZoneDateTimeParts(utcMillis, zone)
            : (() => {
                const date = new Date(utcMillis);
                return {
                    year: date.getFullYear(),
                    month: date.getMonth() + 1,
                    day: date.getDate(),
                    hour: date.getHours(),
                    minute: date.getMinutes(),
                    second: date.getSeconds(),
                };
            })();
        return `${parts.year}-${pad2(parts.month)}-${pad2(parts.day)}T${pad2(parts.hour)}:${pad2(parts.minute)}`;
    } catch (_error) {
        const fallback = new Date(utcMillis);
        return `${fallback.getFullYear()}-${pad2(fallback.getMonth() + 1)}-${pad2(fallback.getDate())}T${pad2(fallback.getHours())}:${pad2(fallback.getMinutes())}`;
    }
}

export function zonedWallTimeToIso(localValue, timeZone) {
    if (typeof localValue !== 'string' || !localValue.trim()) return null;
    const match = DATETIME_LOCAL_PATTERN.exec(localValue.trim());
    if (!match) return null;

    const [, yearStr, monthStr, dayStr, hourStr, minuteStr, secondStr = '0'] = match;
    const year = Number(yearStr);
    const month = Number(monthStr) - 1;
    const day = Number(dayStr);
    const hour = Number(hourStr);
    const minute = Number(minuteStr);
    const second = Number(secondStr);
    if (!isValidLocalDateTime(year, month, day, hour, minute, second)) {
        return null;
    }
    const naiveUtcMillis = Date.UTC(year, month, day, hour, minute, second);
    if (!Number.isFinite(naiveUtcMillis)) return null;

    if (typeof timeZone !== 'string' || !timeZone.trim()) {
        return new Date(naiveUtcMillis).toISOString();
    }

    try {
        let offset = getZoneOffsetMs(naiveUtcMillis, timeZone);
        let utcMillis = naiveUtcMillis - offset;
        // Second pass settles around DST transitions where the first guess sat
        // in a different offset bucket than the final wall time.
        offset = getZoneOffsetMs(utcMillis, timeZone);
        utcMillis = naiveUtcMillis - offset;
        if (!matchesRequestedWallTime(utcMillis, timeZone, {
            year,
            month: month + 1,
            day,
            hour,
            minute,
            second,
        })) {
            // Spring-forward gaps produce a nearby valid wall time instead of
            // the one the user entered; reject those impossible local times.
            return null;
        }
        const result = new Date(utcMillis);
        if (Number.isNaN(result.getTime())) return null;
        return result.toISOString();
    } catch (_error) {
        // Unknown/invalid IANA id — fall back to browser-local interpretation
        // so the form can still submit and the backend can reject.
        const fallback = new Date(localValue);
        return Number.isNaN(fallback.getTime()) ? null : fallback.toISOString();
    }
}
